import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'src/routes/paths';
import { useLocales } from 'src/locales';
import SvgColor from 'src/components/svg-color';
import path from 'path-browserify';

// ----------------------------------------------------------------------

const icono = (nombre) => (
  <SvgColor src={`/assets/icons/navbar/${nombre}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONOS = {
  job: icono('ic_job'),
  blog: icono('ic_blog'),
  chat: icono('ic_chat'),
  mail: icono('ic_mail'),
  user: icono('ic_user'),
  file: icono('ic_file'),
  lock: icono('ic_lock'),
  tour: icono('ic_tour'),
  order: icono('ic_order'),
  label: icono('ic_label'),
  blank: icono('ic_blank'),
  kanban: icono('ic_kanban'),
  folder: icono('ic_folder'),
  banking: icono('ic_banking'),
  booking: icono('ic_booking'),
  invoice: icono('ic_invoice'),
  product: icono('ic_product'),
  calendar: icono('ic_calendar'),
  disabled: icono('ic_disabled'),
  external: icono('ic_external'),
  menuItem: icono('ic_menu_item'),
  ecommerce: icono('ic_ecommerce'),
  analytics: icono('ic_analytics'),
  dashboard: icono('ic_dashboard'),
};

const obtenerRolesDeSessionStorage = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user'));
    return Array.isArray(user?.roles) ? user.roles : [];
  } catch (e) {
    return [];
  }
};

// ----------------------------------------------------------------------
// Helpers

const tieneRol = (roles, rol) => roles.includes(rol);
const tieneAlgunRol = (roles, rolesPermitidos) => rolesPermitidos.some((r) => roles.includes(r));

// Dedup simple por subheader + path (evita repetir secciones iguales)
const deduplicarSecciones = (secciones) => {
  const vistos = new Set();
  return secciones.filter((s) => {
    const llave = `${s.subheader}::${s.items?.[0]?.path ?? ''}`;
    if (vistos.has(llave)) return false;
    vistos.add(llave);
    return true;
  });
};

// ----------------------------------------------------------------------
// Secciones por rol (reutilizables)

const crearSeccionesVentanilla = (t) => [
  {
    subheader: t('PANEL PRINCIPAL'),
    items: [
      // {
      //   title: t('Seguimiento de trámites'),
      //   path: paths.dashboard.seguimientoTramites.root,
      //   icon: ICONOS.folder,
      // },
      // {
      //   title: t('Lista de Seguimientos'),
      //   path: paths.dashboard.seguimientoTramites.listado,
      //   icon: ICONOS.folder,
      // },
      {
        title: t('Lista de Seguimientos'),
        path: paths.dashboard.seguimientoTramites.listado,
        icon: ICONOS.folder,
      },
    ],
  },
  // se esta comentando la parte de esto de abajo para ventanilla, luego se descomentara cuando migren los chicos
  {
    subheader: t('TRAMITES'),
    items: [
      {
        title: t('Crear tramite'),
        path: paths.dashboard.tray.root,
        icon: ICONOS.folder,
        children: [
          { title: t('Crear Tramites Oficiales'), path: paths.dashboard.tray.mis_tramites },
          { title: t('Crear Tramite Para Revisión'), path: paths.dashboard.tray.registrar },
          { title: t('Lista de Tramites'), path: paths.dashboard.tray.earrings },
        ],
      },
    ],
  },
  {
    subheader: t('TRAMITES'),
    items: [
      {
        title: t('Tramite y Requisito'),
        path: paths.dashboard.tramitesrequisitos.root,
        icon: ICONOS.blog,
        children: [{ title: t('Agregar Nuevo'), path: paths.dashboard.tramitesrequisitos.root }],
      },
    ],
  },
  // {
  //   subheader: t('PROCESOS'),
  //   items: [
  //     {
  //       title: t('Procesos'),
  //       path: paths.dashboard.procesos.root,
  //       icon: ICONOS.user,
  //       children: [{ title: t('rutas'), path: paths.dashboard.procesos.root }],
  //     },
  //   ],
  // },
];

const crearSeccionesMigrador = (t) => [
  {
    subheader: t('PANEL PRINCIPAL'),
    items: [
      {
        title: t('Lista de Seguimientos'),
        path: paths.dashboard.seguimientoTramites.listado,
        icon: ICONOS.folder,
      },
    ],
  },
  {
    subheader: t('TRAMITES'),
    items: [
      {
        title: t('Crear tramite'),
        path: paths.dashboard.tray.root,
        icon: ICONOS.folder,
        children: [
          // { title: t('Crear Tramite'), path: paths.dashboard.tray.mis_tramites },
          // { title: t('Crear Tramite Para Revisión'), path: paths.dashboard.tray.registrar },
          {
            title: t('Registrar Trámite Histórico'),
            path: paths.dashboard.tray.registrarHistorico,
          },
          { title: t('Lista de Tramites'), path: paths.dashboard.tray.earrings },
        ],
      },
    ],
  },
];

const crearSeccionesRevisionRecibo = (t) => [
  {
    subheader: t('BUSCADOR TICKETS Y PERSONAS'),
    items: [
      {
        title: t('Buscador'),
        path: paths.dashboard.tickets.root,
        icon: ICONOS.ecommerce,
        children: [
          { title: t('Buscar ticket'), path: paths.dashboard.tickets.confirmar },
          { title: t('Buscar personas'), path: paths.dashboard.tickets.root },
        ],
      },
    ],
  },
];

const crearSeccionesCajaPagadora = (t) => [
  {
    subheader: t('TICKETS Y VALORADAS'),
    items: [
      {
        title: t('Tickets y valoradas'),
        path: paths.dashboard.tickets.root,
        icon: ICONOS.ecommerce,
        children: [
          { title: t('Registrar'), path: paths.dashboard.tickets.root },
          { title: t('Listar'), path: paths.dashboard.tickets.listar },
        ],
      },
    ],
  },
];

const crearSeccionesTecnicoAdministrativo = (t) => [
  {
    subheader: t('HOJA DE TRAMITE ADMINISTRATIVOS'),
    items: [
      {
        title: t('Hoja de tramite'),
        path: paths.dashboard.hoja_de_ruta_usuario.root,
        icon: ICONOS.blog,
        children: [
          { title: t('Listado Tramites'), path: paths.dashboard.hoja_de_ruta_usuario.listado },
        ],
      },
    ],
  },
];

const crearSeccionesPorDefecto = (t) => [
  {
    subheader: t('PANEL PRINCIPAL'),
    items: [
      {
        title: t('Inicio'),
        path: paths.dashboard.root,
        icon: ICONOS.dashboard,
      },
    ],
  },
];

// Super admin: tu menú completo
const crearSeccionesSuperAdmin = (t, roles = []) => [
  {
    subheader: t('PANEL PRINCIPAL'),
    items: [
      { title: t('Inicio'), path: paths.dashboard.root, icon: ICONOS.dashboard },
      {
        title: t('Seguimiento de trámites'),
        path: paths.dashboard.seguimientoTramites.root,
        icon: ICONOS.folder,
      },
      {
        title: t('Lista de Seguimientos'),
        path: paths.dashboard.seguimientoTramites.listado,
        icon: ICONOS.folder,
      },
    ],
  },
  {
    subheader: t('TRAMITES'),
    items: [
      {
        title: t('Crear tramite'),
        path: paths.dashboard.tray.root,
        icon: ICONOS.folder,
        children: [
          { title: t('Crear Tramite'), path: paths.dashboard.tray.mis_tramites },
          { title: t('Crear Tramite Para Revisión'), path: paths.dashboard.tray.registrar },
          ...(tieneRol(roles, 'migrador')
            ? [
                {
                  title: t('Registrar Trámite Histórico'),
                  path: paths.dashboard.tray.registrarHistorico,
                },
              ]
            : []),
          { title: t('Lista de Tramites'), path: paths.dashboard.tray.earrings },
        ],
      },
    ],
  },
  {
    subheader: t('TRAMITES'),
    items: [
      {
        title: t('Tramite y Requisito'),
        path: paths.dashboard.tramitesrequisitos.root,
        icon: ICONOS.blog,
        children: [{ title: t('Agregar Nuevo'), path: paths.dashboard.tramitesrequisitos.root }],
      },
    ],
  },
  {
    subheader: t('PROCESOS'),
    items: [
      {
        title: t('Procesos'),
        path: paths.dashboard.procesos.root,
        icon: ICONOS.user,
        children: [{ title: t('rutas'), path: paths.dashboard.procesos.root }],
      },
    ],
  },
  {
    subheader: t('TICKETS Y VALORADAS'),
    items: [
      {
        title: t('Tickets y valoradas'),
        path: paths.dashboard.tickets.root,
        icon: ICONOS.ecommerce,
        children: [
          { title: t('Registrar'), path: paths.dashboard.tickets.root },
          { title: t('Listar'), path: paths.dashboard.tickets.listar },
          { title: t('Confirmar ticket'), path: paths.dashboard.tickets.confirmar },
        ],
      },
    ],
  },
  ...crearSeccionesTecnicoAdministrativo(t),
];

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const navigate = useNavigate();
  const [rolesUsuario, setRolesUsuario] = useState([]);

  useEffect(() => {
    const roles = obtenerRolesDeSessionStorage();
    setRolesUsuario(roles);

    if (roles.includes('posgraduante')) {
      navigate(paths.dashboard.tramites_usuarios.root);
    }
  }, [navigate]);

  const data = useMemo(() => {
    // 1) Super Admin
    if (tieneRol(rolesUsuario, 'super_admin')) {
      return crearSeccionesSuperAdmin(t, rolesUsuario);
    }

    // 2) Posgraduante (menú exclusivo)
    if (tieneRol(rolesUsuario, 'posgraduante')) {
      return [
        {
          subheader: t('PANEL POSGRADUANTE'),
          items: [
            {
              title: t('Todos Mis Trámites'),
              path: paths.dashboard.tramites_usuarios.root,
              icon: ICONOS.dashboard,
            },
          ],
        },
      ];
    }

    // 3) Multi-rol: se suman secciones
    const secciones = [];

    const esVentanilla = tieneRol(rolesUsuario, 'ventanilla');
    const esMigrador = tieneRol(rolesUsuario, 'migrador');
    const esTecnicoAdministrativo = tieneAlgunRol(rolesUsuario, [
      'tecnico_administrativo',
      'secretaria',
    ]);

    // Si quieres que ventanilla también tenga "Inicio", quita esta condición.
    if (!esVentanilla && !esMigrador && !esTecnicoAdministrativo) {
      secciones.push(...crearSeccionesPorDefecto(t));
    }

    if (esVentanilla) secciones.push(...crearSeccionesVentanilla(t));
    if (esMigrador) secciones.push(...crearSeccionesMigrador(t));
    if (tieneRol(rolesUsuario, 'revision_recibo'))
      secciones.push(...crearSeccionesRevisionRecibo(t));
    if (tieneRol(rolesUsuario, 'caja_pagadora')) secciones.push(...crearSeccionesCajaPagadora(t));
    if (esTecnicoAdministrativo) secciones.push(...crearSeccionesTecnicoAdministrativo(t));

    const seccionesFinales = secciones.length ? secciones : crearSeccionesPorDefecto(t);

    return deduplicarSecciones(seccionesFinales);
  }, [t, rolesUsuario]);

  return data;
}
